import React, { useEffect, useRef, useState } from 'react';
import { aiApi, aiError, changedMuch, useAiStatus } from '../../lib/ai';
import './Ai.css';

const IDLE_MS = 1500;

/**
 * "Suggested: Hardware · High — Use", under Create Ticket's description.
 *
 * Gordon, 3 November: once there is a title and a description and the
 * person has stopped typing for a moment (1.5 s), Claude is asked - at low
 * effort - which category and priority it sounds like, with a one-line
 * reason. AT MOST ONCE PER DRAFT: asked again only when the words have
 * changed substantially (lib/ai changedMuch), never for a typo. NEVER ON
 * SUBMIT: the form passes `busy` while it sends, and nothing is asked while
 * it is set. "Use" fills ONLY what is still empty - a category nobody chose,
 * a priority nobody touched - and nothing is ever set without it.
 *
 * Drawn only when GET /ai/status says this person may (IT staff and
 * administrators by default, all staff when the switch says so, never a
 * contractor) and the server is set up; otherwise nothing at all. A limit
 * reached or Claude unreachable is one quiet line; the form carries on.
 *
 *   title, description   the form's words
 *   empty                { category, priority } - which are still empty
 *   onUse(suggestion)    fill them: { category: { id, name }, priority }
 *   busy                 the form is submitting: ask nothing
 */
export default function AiSuggestion({ title, description, empty, onUse, busy }) {
  const status = useAiStatus();
  const allowed = Boolean(status?.can?.suggest);
  const [shown, setShown] = useState(null);       // { category, priority, reason }
  const [problem, setProblem] = useState(null);
  const [used, setUsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const askedFor = useRef(null);                  // the text last asked about
  const ask = useRef(0);

  const t = String(title || '').trim();
  const d = String(description || '').trim();
  const text = `${t}\n${d}`;
  const ready = allowed && !busy && t.length >= 3 && d.length >= 10;

  useEffect(() => {
    if (!ready) return undefined;
    if (askedFor.current !== null && !changedMuch(askedFor.current, text)) return undefined;
    const timer = setTimeout(() => {
      const n = ++ask.current;
      askedFor.current = text;
      setProblem(null);
      aiApi.suggest(t, d)
        .then((s) => {
          if (n !== ask.current) return;
          setShown(s && (s.category || s.priority) ? s : null);
          setUsed(false);
          setDismissed(false);
        })
        .catch((err) => {
          if (n !== ask.current) return;
          setShown(null);
          setProblem(aiError(err, 'No suggestion this time.'));
        });
    }, IDLE_MS);
    return () => clearTimeout(timer);
  }, [ready, text, t, d]);

  // Submitting: forget any answer still on its way.
  useEffect(() => { if (busy) ask.current += 1; }, [busy]);

  if (!allowed || dismissed) return null;
  if (problem && !shown) return <p className="ai-suggest-quiet" role="status" data-ai="suggest-problem">{problem}</p>;
  if (!shown) return null;

  const words = [shown.category?.name, shown.priority?.name].filter(Boolean).join(' · ');
  const fills = (shown.category && empty.category) || (shown.priority && empty.priority);
  return (
    <div className="ai-suggest" role="status" aria-live="polite" data-ai="suggest">
      <span className="ai-badge" title="Suggested by Claude (AI)">AI</span>
      <span>Suggested: <span className="ai-suggest-what">{words}</span></span>
      <span className="ai-suggest-actions">
        {used ? <span className="ai-suggest-quiet">Used</span> : (
          <button type="button" className="ai-link-btn" disabled={!fills}
                  title={fills ? 'Fills the category and priority you have not chosen yet' : 'You have already chosen both'}
                  onClick={() => { onUse(shown); setUsed(true); }}>
            Use
          </button>
        )}
        <button type="button" className="ai-x" aria-label="Dismiss the suggestion" onClick={() => setDismissed(true)}>×</button>
      </span>
      {shown.reason && <span className="ai-suggest-why">{shown.reason}</span>}
    </div>
  );
}
